import { Box, Stack, Typography } from '@mui/material'
import { VirtualFurnitureRoomRemovableOption } from 'constants/virtualFurniture'
import { useGetAssignmentStagingDetails } from 'dataQueries/assignmentDetails.query'
import { AssignmentStagingDetailsDTO, StagingType } from 'models/virtualStaging/staging'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { GRAY_700 } from 'constants/styling/theme'
import { DetailBooleanField, DetailCommentBlock, DetailField, DetailSection, RendererQueryState } from '../common'
import { AssignmentAddonsSection } from './shared/AssignmentAddonsSection.component'
import { UploadedVisualsSection } from '../common/UploadedVisualsSection.component'
import { useAssignmentDetailDrawerWorkspaceId } from '../AssignmentDetailDrawer.context'

import { AssignmentRendererProps } from './AssignmentRenderer.types'

/**
 * Renderer for Virtual Staging assignment details.
 * Handles both BKBN (boolean wall/floor) and Custom (string template wall/floor) variants.
 * Displays overall staging info and per-file configuration sections.
 */
export const VirtualStagingDetailsRenderer: FC<AssignmentRendererProps> = ({ assignment, order, mainProductId }) => {
  const { t } = useTranslation([
    'gallery',
    'virtual_furniture_room_type',
    'virtual_furniture_room_removable_option',
    'virtual_furniture_style_category',
  ])

  const workspaceId = useAssignmentDetailDrawerWorkspaceId()
  const {
    data: stagingDetails,
    isLoading,
    error,
    refetch,
  } = useGetAssignmentStagingDetails(assignment.id, workspaceId)

  // 404 means no staging details saved yet — not a real error
  const isNotFound = (error as { status?: number })?.status === 404
    || (error as { response?: { status?: number } })?.response?.status === 404
  const isError = !!error && !isNotFound

  useEffect(() => {
    if (isNotFound) {
      logger.info('VirtualStagingDetailsRenderer: staging details 404 (no data yet)', undefined, { context: { component: 'VirtualStagingDetailsRenderer', metadata: { assignmentId: assignment.id, stage: assignment.stage } } })
    }
  }, [isNotFound, assignment.id, assignment.stage])

  useEffect(() => {
    if (!isError) return
    logger.error('VirtualStagingDetailsRenderer: failed to load staging details', error instanceof Error ? error : undefined, {
      context: { component: 'VirtualStagingDetailsRenderer', metadata: { assignmentId: assignment.id, workspaceId, stage: assignment.stage } },
    })
  }, [isError, error, assignment.id, assignment.stage, workspaceId])

  const entries = useMemo(() => {
    if (!stagingDetails) return []
    return Object.entries(stagingDetails)
      .filter((entry): entry is [string, AssignmentStagingDetailsDTO] => !!entry[1])
  }, [stagingDetails])

  const fileCount = entries.length

  if (isNotFound) {
    return (
      <DetailSection title={t('gallery:assignment_details.virtual_staging.overall.no_details_title')}>
        <DetailCommentBlock>{t('gallery:assignment_details.virtual_staging.overall.no_details_message')}</DetailCommentBlock>
      </DetailSection>
    )
  }

  return (
    <RendererQueryState
      assignmentId={assignment.id}
      isLoading={isLoading}
      isError={isError && !isNotFound}
      onRetry={refetch}
    >

      <DetailSection title={t('gallery:assignment_details.virtual_staging.overall.title')}>
        <DetailField
          label={t('gallery:assignment_details.virtual_staging.overall.number_of_files')}
          value={fileCount}
        />
      </DetailSection>

      {entries.map(([imageId, details], index) => (
        <VirtualStagingFileSection
          key={imageId}
          details={details}
          fileNumber={index + 1}
          t={t}
        />
      ))}

      {entries.some(([, details]) => !!details.comment) &&
        <DetailSection title={t('gallery:assignment_details.virtual_staging.special_request')}>
          {entries.map(([imageId, details]) => (
            !!details.comment &&
            <DetailCommentBlock key={imageId}>
              {details.comment}
            </DetailCommentBlock>
          ))}
        </DetailSection>
      }

      <AssignmentAddonsSection assignment={assignment} order={order} mainProductId={mainProductId} />

      <UploadedVisualsSection assignmentId={assignment.id} />

    </RendererQueryState>
  )
}

// -- Per-file section sub-component --

interface FileSectionProps {
  details: AssignmentStagingDetailsDTO
  fileNumber: number
  t: (key: string, options?: Record<string, unknown>) => string
}

const VirtualStagingFileSection: FC<FileSectionProps> = ({ details, fileNumber, t }) => {
  const firstRoom = details.roomStaging?.[0]
  const roomType = firstRoom?.roomType
  const roomCount = firstRoom?.count
  const furnitureTemplate = firstRoom?.furnitureTemplate
  const furnitureTemplateName = firstRoom?.furnitureStyleName
  const furnitureTemplateThumbnailUrl = firstRoom?.furnitureThumbnailUrl
  const decorations = details.decorations?.join(', ')

  return (
    <DetailSection title={t('gallery:assignment_details.virtual_staging.file_section', { number: fileNumber })}>

      <DetailField
        label={t('gallery:assignment_details.virtual_staging.room_type')}
        value={roomType ? t(`virtual_furniture_room_type:${roomType}`) : undefined}
      />

      <DetailField
        label={t('gallery:assignment_details.virtual_staging.room_count')}
        value={roomCount}
      />

      <DetailField
        label={t('gallery:assignment_details.virtual_staging.elements_to_remove')}
        value={getRemoveObjectsLabel(details.removeObjects, t)}
      />

      <DetailField
        label={t('gallery:assignment_details.virtual_staging.furniture_template')}
        hidden={!furnitureTemplateThumbnailUrl && !furnitureTemplateName && !furnitureTemplate}
      >
        <StyleThumbnail
          thumbnailUrl={furnitureTemplateThumbnailUrl}
          name={furnitureTemplateName ? t(`virtual_furniture_style_category:${furnitureTemplateName}`) : undefined}
          fallbackValue={furnitureTemplate}
        />
      </DetailField>

      <DetailField
        label={t('gallery:assignment_details.virtual_staging.decoration_template')}
        value={decorations || undefined}
      />

      {details.type === StagingType.BKBN && (
        <>
          <DetailBooleanField
            label={t('gallery:assignment_details.virtual_staging.wall_colour_template')}
            value={details.wallPainting}
          />

          <DetailBooleanField
            label={t('gallery:assignment_details.virtual_staging.floor_template')}
            value={details.floor}
          />
        </>
      )}

      {details.type === StagingType.CUSTOM && (
        <>
          <DetailField
            label={t('gallery:assignment_details.virtual_staging.wall_colour_template')}
            hidden={!details.wallPaintingThumbnailUrl && !details.wallPainting}
          >
            <StyleThumbnail
              thumbnailUrl={details.wallPaintingThumbnailUrl}
              name={details.wallPaintingStyleName}
              fallbackValue={details.wallPainting}
            />
          </DetailField>

          <DetailField
            label={t('gallery:assignment_details.virtual_staging.floor_template')}
            hidden={!details.floorThumbnailUrl && !details.floor}
          >
            <StyleThumbnail
              thumbnailUrl={details.floorThumbnailUrl}
              name={details.floorStyleName}
              fallbackValue={details.floor}
            />
          </DetailField>
        </>
      )}

      {!!details.addons?.length &&
        <DetailField
          label={t('gallery:assignment_details.virtual_staging.addons')}
          value={details.addons.map(a => a.addonType).join(', ')}
        />
      }

    </DetailSection>
  )
}

// -- Thumbnail with onError fallback --

interface StyleThumbnailProps {
  thumbnailUrl?: string | null
  name?: string | null
  fallbackValue?: string | null
}

const StyleThumbnail: FC<StyleThumbnailProps> = ({ thumbnailUrl, name, fallbackValue }) => {
  const [imgFailed, setImgFailed] = useState(false)

  const showImage = !!thumbnailUrl && !imgFailed

  return (
    <Stack alignItems="flex-start">

      {showImage &&
        <Box
          component="img"
          src={thumbnailUrl}
          alt={name ?? fallbackValue ?? ''}
          onError={() => setImgFailed(true)}
          sx={{
            width: '12.8rem',
            height: '7.2rem',
            borderRadius: '.8rem',
            objectFit: 'cover',
          }}
        />
      }

      {!!name &&
        <Typography
          fontSize="1.2rem"
          color={GRAY_700}
          fontWeight={450}
        >
          {name}
        </Typography>
      }

      {!showImage && !name && !!fallbackValue &&
        <Typography>{fallbackValue}</Typography>
      }

    </Stack>
  )
}

function getRemoveObjectsLabel(
  removeObjects: VirtualFurnitureRoomRemovableOption | null | undefined,
  t: (key: string) => string
): string | undefined {
  if (!removeObjects || removeObjects === VirtualFurnitureRoomRemovableOption.NONE) return undefined
  return t(`virtual_furniture_room_removable_option:${removeObjects}`)
}
